import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-validation-summary>` says about itself.
 *
 * This piece exists because of what forms do without it: a save is rejected, one field turns red,
 * the person fixes it, saves again, and a second field turns red. Three round trips to learn three
 * things the server knew all at once. The summary gathers EVERY blocking error from one save and
 * prints them as one list, so the person reads the whole cost of the fix before starting it.
 *
 * The second half is the linking. Each error is a real button, and pressing it scrolls to the field
 * named by its `target` id and puts the caret in it — including into a `caos-*-field`, where the
 * real control lives inside an open shadow root and a plain `.focus()` on the wrapper would land
 * nowhere. An error with no `target` is drawn as plain text, marked static, and taken OUT of the tab
 * order, so a keyboard user is never sent to a control that does nothing.
 *
 * ── Two ways to give it errors, and one of them has nowhere to be declared ──
 * `items` is the property, and it is how an app sets them. But the component ALSO reads `<li>`
 * children out of its own light DOM on connect (their text, plus `data-field` and `data-target`),
 * for a form rendered as markup. Those `<li>`s are read and re-rendered rather than slotted — there
 * is no `<slot>` anywhere in this component — so `slots: []` below is the true answer and the
 * light-DOM route has no field of its own to be declared in. It is written up here and shown in the
 * third example instead.
 *
 * The old hand-kept example gave each item a `label` key; the component's own `CaosValidationItem`
 * has `message`, `field` and `target` and ignores anything else, so those labels drew nothing. The
 * field names are carried across here under `field`, where they are actually read.
 */
const docs: ComponentDocs = {
  aliases: ['error summary', 'form errors', 'validation errors', 'save failed list', 'error list'],

  answers: [
    'tell somebody everything wrong with a form at once',
    'take somebody straight to the field that is wrong',
    'explain why a save was rejected',
    'stop a person fixing one error at a time',
  ],

  placements: ['recordPage', 'appPage', 'screen'],

  inputs: [
    {
      name: 'title',
      attribute: 'title',
      type: 'text',
      description:
        'The heading. Left unset it counts the errors itself — "There are 3 problems to fix", and the ' +
        'singular when there is one — which is a count that cannot disagree with the list under it. ' +
        'Override it only to say something the count cannot, and note that `title` is also the ' +
        'standard HTML attribute, so whatever is set here doubles as the element\'s hover tooltip.',
    },
    {
      // No `attribute`: this is a list of objects. There is no honest way to put one in a string,
      // and the source has no attribute for it either.
      name: 'items',
      type: 'json',
      description:
        'The blocking errors, as `{ message, field?, target? }[]`. `message` is the sentence, `field` ' +
        'is the field name printed in bold ahead of it, and `target` is the id of the element in the ' +
        'host document to scroll to and focus when the error is pressed. An item with no target is ' +
        'drawn as plain text and skipped by the keyboard.',
    },
  ],

  events: [
    {
      name: 'caos-validation-focus',
      when:
        'An error carrying a `target` is pressed — before the summary scrolls to the field and focuses ' +
        'it. It bubbles and crosses the shadow boundary, so a form can listen once at its root.',
      carries: '`{ target, field }` — the id being focused, and the field name if the item carried one.',
    },
  ],

  // No `<slot>` is rendered anywhere in this component. The `<li>` children it reads on connect are
  // read and re-drawn, not slotted — see the header.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-color-danger-bg', controls: 'The pale fill behind the whole summary.' },
    { token: '--caos-color-danger', controls: 'The glyph, and the list markers.' },
    { token: '--caos-color-danger-text', controls: 'The heading and each error link — the on-tint ink, so they hold up in both themes.' },
    { token: '--caos-color-danger-text-muted', controls: 'The field name ahead of each message, and the body ink.' },
    { token: '--caos-radius', controls: 'The corner radius of the summary.' },
    { token: '--caos-radius-sm', controls: 'The corner radius of an error\'s focus ring.' },
    { token: '--caos-focus-ring', controls: 'The ring drawn on an error reached by keyboard.' },
    { token: '--caos-font', controls: 'The typeface the summary is set in.' },
    { token: '--caos-text-row', controls: 'The size of the heading.' },
    { token: '--caos-space-4', controls: 'The horizontal padding, and the indent of the list.' },
    { token: '--caos-space-3', controls: 'The vertical padding, and the gap between the glyph and the content.' },
    { token: '--caos-space-2', controls: 'The gap between the heading and the list.' },
  ],

  // `items` is a property accessor, not a method. Nothing else drives this piece by calling it.
  parts: [
    { part: 'summary', is: 'the alert box' },
    { part: 'icon', is: 'the danger glyph' },
    { part: 'title', is: 'the count line' },
    { part: 'list', is: 'the list of problems' },
    { part: 'error', is: 'one problem, which is the control that jumps to its field' },
  ],

  methods: [],

  placing:
    'At the top of the form it belongs to, above the first field, inside the same region — so that ' +
    'scrolling to a linked field is a scroll DOWN from something the person has just read. It takes ' +
    'the full width it is given. It is drawn only while a save stands rejected: leaving it on screen ' +
    'after the errors are fixed is how a form ends up shouting at somebody who has already done what ' +
    'was asked.',

  whenToUse:
    'A save the server refused, where more than one thing is wrong or where what is wrong is off ' +
    'screen. Reach for it whenever the alternative is letting somebody discover the errors one submit ' +
    'at a time.',

  insteadUse: [
    {
      name: 'ErrorPanel',
      when:
        'The save FAILED rather than being rejected — the request never got a verdict. That is the ' +
        'line between these two: a rejection is a list of things this person can fix now, a failure is ' +
        'a code and a correlation id for somebody else to look up. A rejected save reported as a ' +
        'failure sends a correctable form to the on-call engineer; a failure reported as a rejection ' +
        'leaves somebody re-reading their own input for a mistake that was never there.',
    },
    {
      name: 'NoticeBanner',
      when:
        'There is one thing to say about the page as a whole and it does not belong to a field — a ' +
        'closed period, a locked record, a decision to make before continuing. The banner says one ' +
        'thing; this piece enumerates and links.',
    },
    {
      name: 'BaseField',
      when:
        'Exactly one field is wrong and it is on screen. Every field draws its own message under ' +
        'itself and turns red when invalid, which is closer to the problem than a summary at the top ' +
        'of the form — use both together when the form is long enough to scroll.',
    },
    {
      name: 'AccessDeniedPanel',
      when:
        'The save was refused because of a missing grant. Nothing in the input is wrong, so there is ' +
        'no list to fix and nothing to link to.',
    },
    {
      name: 'Toast',
      when:
        'The save SUCCEEDED, or failed with nothing for the person to correct. A list of errors must ' +
        'stay on screen while they are being worked through, which is the one thing a toast will not do.',
    },
  ],

  accessibility:
    'The summary is a `role="alert"` labelled by its own heading, so the whole failure is announced when ' +
    'it appears rather than when somebody happens to reach it, and the glyph is hidden because the ' +
    'heading says the same thing. Each error is a real button inside a real list, so the count is ' +
    'announced and every error is reachable by keyboard — and an error with no target is marked static ' +
    'and given `tabindex="-1"`, which is the deliberate part: a keyboard user is never landed on ' +
    'something that looks pressable and does nothing. Focus moves INTO the field, delegating through a ' +
    'field wrapper\'s shadow root to the real control. Left to the author: giving each item a `target`, ' +
    'and taking the summary off the page once the errors are fixed.',

  examples: [
    {
      key: 'linked',
      label: 'Every error, linked to its field',
      summary:
        'One rejected save reported whole: three errors, each naming its field and each pressable — ' +
        'pressing one scrolls to that field and puts the caret in it.',
      placement: {
        component: 'caos-validation-summary',
        inputs: {
          title: 'This order could not be saved',
          items: [
            { field: 'Account', message: 'Choose an account.', target: 'vs-account' },
            { field: 'Due date', message: 'Must be in the future.', target: 'vs-due-date' },
            { field: 'Amount', message: 'Enter an amount before submitting for review.', target: 'vs-amount' },
          ],
        },
      },
      host: {
        summary:
          'The surrounding page supplies the three fields these errors point at, so pressing an error ' +
          'really moves the caret to its field instead of doing nothing.',
        run: (stage: HTMLElement): void => {
          const summary = stage.querySelector('caos-validation-summary');
          if (!summary) return;
          const form = document.createElement('div');
          form.style.display = 'grid';
          form.style.gap = '12px';
          form.style.marginTop = '16px';
          form.style.maxWidth = '340px';
          const fields: readonly (readonly [string, string])[] = [
            ['vs-account', 'Account'],
            ['vs-due-date', 'Due date'],
            ['vs-amount', 'Amount'],
          ];
          for (const [id, label] of fields) {
            const field = document.createElement('caos-text-field');
            field.id = id;
            field.setAttribute('label', label);
            field.setAttribute('invalid', '');
            form.append(field);
          }
          summary.after(form);
        },
      },
    },
    {
      key: 'unlinked',
      label: 'Errors with no field to point at',
      summary:
        'What a rejection that does not map onto the form looks like: the messages are drawn as plain ' +
        'text rather than links, and the keyboard skips past them because there is nowhere to go.',
      placement: {
        component: 'caos-validation-summary',
        inputs: {
          items: [
            { message: 'This record is locked while the order it belongs to is being invoiced.' },
            { message: 'The template it was created from is no longer available.' },
          ],
        },
      },
    },
    {
      key: 'markup',
      label: 'Errors written as markup',
      summary:
        'The same list authored as `<li>` children instead of set as a property — read once on connect ' +
        'and re-drawn, for a form rendered on the server rather than assembled in script.',
      placement: {
        component: 'caos-validation-summary',
        children: [
          {
            component: 'li',
            inputs: { 'data-field': 'Account', 'data-target': 'vs-account' },
            children: ['Choose an account.'],
          },
          {
            component: 'li',
            inputs: { 'data-field': 'Due date', 'data-target': 'vs-due-date' },
            children: ['Must be in the future.'],
          },
        ],
      },
    },
  ],
};

export default docs;
